/**
 * Pre-Order Info Popup
 * Shows informational popup after pre-order items are added to cart
 * Displays discount code for pre-order purchases
 * Uses native <dialog> element for proper layering above cart drawer
 */

class PreOrderInfoPopup {
  constructor() {
    this.modal = document.getElementById('preorder-modal');
    if (!this.modal) return;

    this.init();
  }

  init() {
    // Setup event listeners
    this.setupCloseHandlers();
    this.setupCopyButton();

    // Listen for cart updates
    this.listenForCartUpdates();
  }

  /**
   * Listen for cart:update events from Shopify
   * Uses bulletproof variant-level preorder detection
   */
  listenForCartUpdates() {
    document.addEventListener('cart:update', (event) => {
      // Get productId from the event
      const productId = event.detail?.data?.productId;
      if (!productId) return;

      // Skip if this is just a cart update (not an add)
      const source = event.detail?.data?.source;
      if (source === 'cart-items-component') return;

      // Find product form
      const productForm = document.querySelector(
        `product-form-component[data-product-id="${productId}"]`
      );
      if (!productForm) return;

      // BULLETPROOF CHECK: Use variant-level preorder map
      const variantInput = productForm.querySelector('input[name="id"]');
      if (!variantInput) return;

      const currentVariantId = variantInput.value;
      const preorderMapJson = productForm.dataset.variantPreorderMap;

      let isPreorder = false;

      if (preorderMapJson) {
        try {
          const preorderMap = JSON.parse(preorderMapJson);
          // Check if the ACTUAL variant being added is a preorder
          isPreorder = preorderMap[currentVariantId] === true;
        } catch (e) {
          // Fallback to legacy check if JSON parsing fails
          isPreorder = productForm.dataset.isPreorder === 'true';
        }
      } else {
        // Fallback for forms without the map (e.g., quick-add)
        isPreorder = productForm.dataset.isPreorder === 'true';
      }

      if (isPreorder) {
        // Small delay to ensure this dialog opens AFTER the cart drawer
        // Both are in the top layer, but the last one opened appears on top
        setTimeout(() => {
          this.showModal();
        }, 50);
      }
    });
  }

  /**
   * Show the modal using native dialog API
   */
  showModal() {
    // Safety checks to prevent InvalidStateError
    if (!this.modal) return;
    if (this.modal.open) return;
    if (!document.body.contains(this.modal)) return;

    try {
      // Use native showModal() - this puts it in the top layer above cart drawer
      this.modal.showModal();

      // Focus management for accessibility
      const closeButton = this.modal.querySelector('[data-preorder-close]');
      if (closeButton) {
        closeButton.focus();
      }

      // Track analytics
      this.trackEvent('preorder_info_shown');
    } catch (e) {
      // Silently fail if dialog can't be shown (e.g., element removed from DOM)
      console.warn('PreOrder popup could not be shown:', e.message);
    }
  }

  /**
   * Hide the modal using native dialog API
   */
  hideModal() {
    if (!this.modal || !this.modal.open) return;

    this.modal.close();
  }

  /**
   * Setup close button handlers
   */
  setupCloseHandlers() {
    // Close button clicks
    const closeButtons = this.modal.querySelectorAll('[data-preorder-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.hideModal();
        this.trackEvent('preorder_info_closed');
      });
    });

    // Click on backdrop (outside dialog content) to close
    this.modal.addEventListener('click', (e) => {
      // Only close if clicking directly on the dialog (backdrop area)
      if (e.target === this.modal) {
        this.hideModal();
        this.trackEvent('preorder_info_closed');
      }
    });

    // Native dialog handles ESC key automatically, but we track the close event
    this.modal.addEventListener('close', () => {
      this.trackEvent('preorder_info_closed');
    });
  }

  /**
   * Setup copy button functionality
   */
  setupCopyButton() {
    const copyButton = this.modal.querySelector('[data-preorder-copy]');
    const codeInput = this.modal.querySelector('#preorder-code-input');
    const copyText = this.modal.querySelector('[data-copy-text]');
    const copySuccess = this.modal.querySelector('[data-copy-success]');

    if (!copyButton || !codeInput) return;

    copyButton.addEventListener('click', async () => {
      try {
        // Copy to clipboard
        await navigator.clipboard.writeText(codeInput.value);

        // Show success state
        if (copyText) copyText.hidden = true;
        if (copySuccess) copySuccess.hidden = false;

        // Reset after 2 seconds
        setTimeout(() => {
          if (copyText) copyText.hidden = false;
          if (copySuccess) copySuccess.hidden = true;
        }, 2000);

        this.trackEvent('preorder_code_copied');
      } catch (err) {
        // Fallback for older browsers
        codeInput.select();
        document.execCommand('copy');
      }
    });

    // Also allow clicking on input to select all
    codeInput.addEventListener('click', () => {
      codeInput.select();
    });
  }

  /**
   * Track analytics events
   */
  trackEvent(eventName) {
    // Google Analytics 4
    if (window.gtag) {
      gtag('event', eventName, {
        event_category: 'pre_order',
        event_label: 'Pre-Order Info Popup'
      });
    }

    // Facebook Pixel
    if (window.fbq) {
      fbq('trackCustom', eventName);
    }

    // Shopify Analytics
    if (window.ShopifyAnalytics) {
      window.ShopifyAnalytics.lib.track(eventName, {
        category: 'pre_order'
      });
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PreOrderInfoPopup();
  });
} else {
  new PreOrderInfoPopup();
}
